import OperandStack from './OperandStack';
import OpCodes from '../OpCodes';
import { ValueType, BlockType, ImmediateType, ValueTypeDescriptor, BlockTypeDescriptor, OpCodeDef, isConcreteRefType, refType, ConcreteRefTypeDescriptor } from '../types';
import { OperandStackBehavior, OperandStackType, ControlFlowType } from './types';
import Immediate from '../Immediate';
import ControlFlowBlock from './ControlFlowBlock';
import ImportBuilder from '../ImportBuilder';
import FuncTypeBuilder from '../FuncTypeBuilder';
import LocalBuilder from '../LocalBuilder';
import GlobalBuilder from '../GlobalBuilder';
import FunctionParameterBuilder from '../FunctionParameterBuilder';
import FuncTypeSignature from '../FuncTypeSignature';
import VerificationError from './VerificationError';
import type StructTypeBuilder from '../StructTypeBuilder';
import type ArrayTypeBuilder from '../ArrayTypeBuilder';

/**
 * Provides access to module type definitions for GC opcode verification.
 * The verifier uses this to look up struct field types and array element types.
 */
export interface TypeResolver {
  getStructType(typeIndex: number): StructTypeBuilder | null;
  getArrayType(typeIndex: number): ArrayTypeBuilder | null;
  getFuncType?(typeIndex: number): { parameterTypes: ValueTypeDescriptor[]; returnTypes: ValueTypeDescriptor[] } | null;
  getTypeKind?(typeIndex: number): 'struct' | 'array' | 'func' | null;
  getSuperTypes?(typeIndex: number): number[];
}

// Map heap type encoding values to their corresponding ValueType descriptors
const heapTypeToValueType: Record<number, ValueTypeDescriptor> = {
  0x70: ValueType.FuncRef,
  0x6f: ValueType.ExternRef,
  0x6e: ValueType.AnyRef,
  0x6d: ValueType.EqRef,
  0x6c: ValueType.I31Ref,
  0x6b: ValueType.StructRef,
  0x6a: ValueType.ArrayRef,
  0x71: ValueType.NullRef,
  0x73: ValueType.NullFuncRef,
  0x72: ValueType.NullExternRef,
};

const _refTypes = new Set<ValueTypeDescriptor>([
  ValueType.FuncRef, ValueType.ExternRef, ValueType.AnyRef,
  ValueType.EqRef, ValueType.I31Ref, ValueType.StructRef,
  ValueType.ArrayRef, ValueType.NullRef, ValueType.NullFuncRef,
  ValueType.NullExternRef,
]);

// Proper lookup map from OperandStackType string to ValueType descriptor
const valueTypeByName: Record<string, ValueTypeDescriptor> = {
  Int32: ValueType.Int32,
  Int64: ValueType.Int64,
  Float32: ValueType.Float32,
  Float64: ValueType.Float64,
  V128: ValueType.V128,
  FuncRef: ValueType.FuncRef,
  ExternRef: ValueType.ExternRef,
  AnyRef: ValueType.AnyRef,
  EqRef: ValueType.EqRef,
  I31Ref: ValueType.I31Ref,
  StructRef: ValueType.StructRef,
  ArrayRef: ValueType.ArrayRef,
};

function _isRefType(vt: ValueTypeDescriptor): boolean {
  return _refTypes.has(vt);
}

export default class OperandStackVerifier {
  _operandStack: OperandStack;
  _instructionCount: number;
  _funcType: FuncTypeSignature;
  _unreachable: boolean;
  _typeResolver: TypeResolver | null;
  _memory64: boolean;

  constructor(funcType: FuncTypeSignature, typeResolver?: TypeResolver, memory64?: boolean) {
    this._operandStack = OperandStack.Empty;
    this._instructionCount = 0;
    this._funcType = funcType;
    this._unreachable = false;
    this._typeResolver = typeResolver || null;
    this._memory64 = memory64 || false;
  }

  get stack(): OperandStack {
    return this._operandStack;
  }

  verifyInstruction(
    controlBlock: ControlFlowBlock,
    opCode: OpCodeDef,
    immediate: Immediate | null
  ): void {
    // After unreachable code (throw, br, return, unreachable), skip verification
    // until the next control flow boundary (end, else, catch, catch_all)
    if (this._unreachable) {
      if (opCode.controlFlow === ControlFlowType.Pop) {
        // Reset unreachable on end — restore stack to block entry
        this._unreachable = false;
        this._operandStack = controlBlock.stack;
        const resultTypes = this._resolveBlockResultTypes(controlBlock);
        for (const rt of resultTypes) {
          this._operandStack = this._operandStack.push(rt);
        }
      }
      this._instructionCount++;
      return;
    }

    let modifiedStack = this._operandStack;
    if (opCode.stackBehavior !== OperandStackBehavior.None) {
      modifiedStack = this._verifyStack(controlBlock, opCode, immediate);
    }

    if (opCode.controlFlow === ControlFlowType.Pop) {
      this._verifyControlFlowPop(controlBlock, modifiedStack);
    } else if (opCode === OpCodes["return"]) {
      modifiedStack = this._verifyReturnValues(modifiedStack, true);
      this._unreachable = true;
    }

    if (immediate && immediate.type === ImmediateType.RelativeDepth) {
      this._verifyBranch(modifiedStack, immediate);
    }
    // Note: BrOnCast branch verification is skipped — br_on_cast carries the
    // narrowed ref type to the target, which requires full GC subtype checking
    // against the block's expected type. The control flow label reference is
    // still tracked by the ControlFlowVerifier.

    // Mark as unreachable after unconditional control transfer
    if (
      opCode === OpCodes["throw"] ||
      opCode === OpCodes.rethrow ||
      opCode === OpCodes.unreachable ||
      opCode === OpCodes.br ||
      opCode === OpCodes.br_table
    ) {
      this._unreachable = true;
    }

    this._operandStack = modifiedStack;
    this._instructionCount++;
  }

  verifyElse(controlBlock: ControlFlowBlock): void {
    // If the if-branch ended in unreachable code, skip stack checks and reset
    if (this._unreachable) {
      this._unreachable = false;
      this._operandStack = controlBlock.stack;
      return;
    }
    const resultTypes = this._resolveBlockResultTypes(controlBlock);
    if (resultTypes.length > 0) {
      // Non-void if block: the true branch must have the result(s) on stack
      let current = this._operandStack;
      for (let i = resultTypes.length - 1; i >= 0; i--) {
        if (current.isEmpty) {
          throw new VerificationError(
            `else: expected ${resultTypes[i].name} on the stack from the if-branch but the stack is empty.`
          );
        }
        if (!this._isAssignableTo(current.valueType, resultTypes[i])) {
          throw new VerificationError(
            `else: expected ${resultTypes[i].name} on the stack but found ${current.valueType.name}.`
          );
        }
        current = current.pop();
      }
      if (current !== controlBlock.stack) {
        throw new VerificationError(
          'else: stack (minus result value) does not match the if-block entry stack.'
        );
      }
    } else {
      // Void if block: stack must match entry
      if (this._operandStack !== controlBlock.stack) {
        throw new VerificationError(
          'else: stack does not match the if-block entry stack.'
        );
      }
    }
    // Reset stack for else branch
    this._operandStack = controlBlock.stack;
  }

  _verifyBranch(stack: OperandStack, immediate: Immediate): void {
    const operands = immediate.operands;
    if (operands.type !== ImmediateType.RelativeDepth) {
      throw new VerificationError('Branch immediate must be a relative depth.');
    }
    const targetBlock = operands.label.block as ControlFlowBlock;
    const targetEntryStack = targetBlock.stack;

    if (targetBlock.isLoop) {
      // Branch to loop header: no result values needed, stack must match entry
      if (targetEntryStack !== stack) {
        throw new VerificationError(
          'Branch to loop: stack does not match the loop entry stack.'
        );
      }
      return;
    }

    const resultTypes = this._resolveBlockResultTypes(targetBlock);
    if (resultTypes.length > 0) {
      // Non-void block: branch must carry the result value(s)
      for (let i = resultTypes.length - 1; i >= 0; i--) {
        if (stack.isEmpty) {
          throw new VerificationError(
            `Branch expects ${resultTypes[i].name} on the stack but the stack is empty.`
          );
        }
        if (!this._isAssignableTo(stack.valueType, resultTypes[i])) {
          throw new VerificationError(
            `Branch expects ${resultTypes[i].name} but found ${stack.valueType.name} on the stack.`
          );
        }
        stack = stack.pop();
      }
    }

    if (targetEntryStack !== stack) {
      throw new VerificationError(
        'Branch: stack does not match the target block entry stack.'
      );
    }
  }

  _verifyReturnValues(stack: OperandStack, pop: boolean = false): OperandStack {
    const remaining = this._getStackValueTypes(stack, stack.length);
    if (remaining.length !== this._funcType.returnTypes.length) {
      if (remaining.length === 0) {
        throw new VerificationError(
          `Function expected to return ${this._formatAndList(this._funcType.returnTypes, (x) => x.name)} ` +
            'but stack is empty.'
        );
      } else if (this._funcType.returnTypes.length === 0) {
        throw new VerificationError(
          `Function does not have any return values but ${this._formatAndList(remaining, (x) => x.name)} ` +
            `was found on the stack.`
        );
      }

      throw new VerificationError(
        `Function return values do not match the items on the stack. ` +
          `Expected: ${this._formatAndList(this._funcType.returnTypes, (x) => x.name)} ` +
          `Found on stack: ${this._formatAndList(remaining, (x) => x.name)}.`
      );
    }

    let errorMessage = '';
    for (let index = 0; index < remaining.length; index++) {
      if (!this._isAssignableTo(remaining[index], this._funcType.returnTypes[index])) {
        errorMessage =
          `A ${this._funcType.returnTypes[index].name} was expected at ${remaining.length - index} ` +
          `but a ${remaining[index].name} was found. `;
      }
    }

    if (errorMessage !== '') {
      throw new VerificationError('Error returning from function: ' + errorMessage);
    }

    if (pop) {
      for (let index = 0; index < remaining.length; index++) {
        stack = stack.pop();
      }
    }

    return stack;
  }

  _resolveBlockResultTypes(block: ControlFlowBlock): ValueTypeDescriptor[] {
    if (block.blockType === BlockType.Void) return [];
    if (typeof block.blockType === 'number') {
      // Type index — look up function type
      if (this._typeResolver?.getFuncType) {
        const ft = this._typeResolver.getFuncType(block.blockType);
        if (ft) return ft.returnTypes;
      }
      return [];
    }
    return [block.blockType as ValueTypeDescriptor];
  }

  _resolveBlockParamTypes(block: ControlFlowBlock): ValueTypeDescriptor[] {
    if (typeof block.blockType === 'number') {
      if (this._typeResolver?.getFuncType) {
        const ft = this._typeResolver.getFuncType(block.blockType);
        if (ft) return ft.parameterTypes;
      }
    }
    return [];
  }

  _verifyControlFlowPop(controlBlock: ControlFlowBlock, stack: OperandStack): void {
    if (controlBlock.depth === 0) {
      this._verifyReturnValues(stack);
    } else {
      const resultTypes = this._resolveBlockResultTypes(controlBlock);
      // Pop result values
      let expected = stack;
      for (let i = resultTypes.length - 1; i >= 0; i--) {
        if (expected.isEmpty) {
          throw new VerificationError(
            `Stack mismatch at end of block (depth ${controlBlock.depth}): expected ${resultTypes[i].name} but stack is empty.`
          );
        }
        if (!this._isAssignableTo(expected.valueType, resultTypes[i])) {
          throw new VerificationError(
            `Stack mismatch at end of block (depth ${controlBlock.depth}): expected ${resultTypes[i].name} but found ${expected.valueType.name}.`
          );
        }
        expected = expected.pop();
      }
      if (controlBlock.stack !== expected) {
        throw new VerificationError(
          `Stack mismatch at end of block (depth ${controlBlock.depth}): ` +
          `expected block's entry stack but got a different stack state.`
        );
      }
    }
  }

  _verifyStack(
    controlFlowBlock: ControlFlowBlock,
    opCode: OpCodeDef,
    immediate: Immediate | null
  ): OperandStack {
    let modifiedStack = this._operandStack;
    const funcType = this._getFuncType(opCode, immediate);

    // Handle ref_null: metadata says Int32 but should push the appropriate ref type
    if (opCode === OpCodes.ref_null && immediate) {
      const heapType = immediate.numericOperands()[0];
      const refVt = (typeof heapType === 'number' && heapTypeToValueType[heapType])
        ? heapTypeToValueType[heapType]
        : ValueType.AnyRef;
      return modifiedStack.push(refVt);
    }

    // Handle ref_is_null: pops any reference type and pushes Int32
    if (opCode === OpCodes.ref_is_null) {
      return modifiedStack.pop().push(ValueType.Int32);
    }

    // Handle ref_func: pushes funcref (metadata says Int32)
    if (opCode === OpCodes.ref_func) {
      return modifiedStack.push(ValueType.FuncRef);
    }

    // Handle struct_new: pop N field values (metadata says Push-only, but it actually pops)
    if (opCode === OpCodes.struct_new && this._typeResolver && immediate) {
      const typeIndex = immediate.numericOperands()[0];
      const structType = this._typeResolver.getStructType(typeIndex);
      if (structType) {
        // Pop field values in reverse order (last field is on top of stack)
        for (let i = structType.fields.length - 1; i >= 0; i--) {
          modifiedStack = modifiedStack.pop();
        }
      }
      // Push concrete ref type
      modifiedStack = modifiedStack.push(refType(typeIndex));
      return modifiedStack;
    }

    // Handle array_new_fixed: pop N element values (metadata says Push-only)
    if (opCode === OpCodes.array_new_fixed && immediate) {
      const arrayOperands = immediate.numericOperands();
      const typeIndex = arrayOperands[0];
      const fixedLength = arrayOperands[1];
      for (let i = 0; i < fixedLength; i++) {
        modifiedStack = modifiedStack.pop();
      }
      modifiedStack = modifiedStack.push(refType(typeIndex));
      return modifiedStack;
    }

    if (
      opCode.stackBehavior === OperandStackBehavior.Pop ||
      opCode.stackBehavior === OperandStackBehavior.PopPush
    ) {
      modifiedStack = this._verifyStackPop(modifiedStack, opCode, funcType);
    }

    if (
      opCode.stackBehavior === OperandStackBehavior.Push ||
      opCode.stackBehavior === OperandStackBehavior.PopPush
    ) {
      modifiedStack = this._stackPush(
        modifiedStack,
        controlFlowBlock,
        opCode,
        immediate,
        funcType
      );
    }

    return modifiedStack;
  }

  _verifyStackPop(
    stack: OperandStack,
    opCode: OpCodeDef,
    funcType: FuncTypeBuilder | null
  ): OperandStack {
    // Pop popOperands first (e.g. call_indirect's table index sits on top of params).
    // Iterate in reverse because the array is in push order (bottom-to-top)
    // but we pop from the top of the stack.
    const pops = opCode.popOperands || [];
    for (let idx = pops.length - 1; idx >= 0; idx--) {
      const x = pops[idx];
      if (x === OperandStackType.Any) {
        stack = stack.pop();
        continue;
      }

      const valueType = valueTypeByName[x];
      if (!this._isAssignableTo(stack.valueType, valueType)) {
        throw new VerificationError(
          `Unexpected type found on stack at offset ${this._instructionCount + 1}. ` +
            `A ${valueType.name} was expected but a ${stack.valueType.name} was found.`
        );
      }
      stack = stack.pop();
    }

    // Then pop function parameter types (for call / call_indirect).
    // Iterate in reverse because parameters are pushed in declaration order
    // (param 0 at bottom, last param on top).
    if (funcType) {
      const params = funcType.parameterTypes;
      for (let idx = params.length - 1; idx >= 0; idx--) {
        const x = params[idx];
        if (!this._isAssignableTo(stack.valueType, x)) {
          throw new VerificationError(
            `Unexpected type found on stack at offset ${this._instructionCount + 1}. ` +
              `A ${x.name} was expected but a ${stack.valueType.name} was found.`
          );
        }
        stack = stack.pop();
      }
    }

    return stack;
  }

  _stackPush(
    stack: OperandStack,
    controlBlock: ControlFlowBlock,
    opCode: OpCodeDef,
    immediate: Immediate | null,
    funcType: FuncTypeBuilder | null
  ): OperandStack {
    const stackStart = stack;
    if (funcType) {
      stack = funcType.returnTypes.reduce((i, x) => {
        return i.push(x);
      }, stack);
    }

    stack = (opCode.pushOperands || []).reduce((i, x) => {
      let valueType: ValueTypeDescriptor;
      if (x !== OperandStackType.Any) {
        valueType = valueTypeByName[x];
      } else {
        const popCount = this._operandStack.length - stackStart.length;
        valueType = this._getStackObjectValueType(opCode, immediate, popCount);
      }
      return i.push(valueType);
    }, stack);

    return stack;
  }

  _getFuncType(opCode: OpCodeDef, immediate: Immediate | null): FuncTypeBuilder | null {
    let funcType: FuncTypeBuilder | null = null;

    if (opCode === OpCodes.call || opCode === OpCodes.return_call) {
      const operands = immediate!.operands;
      if (operands.type !== ImmediateType.Function) {
        throw new VerificationError('Error getting funcType for call, invalid immediate.');
      }
      funcType = operands.func instanceof ImportBuilder
        ? operands.func.data as FuncTypeBuilder
        : operands.func.funcTypeBuilder;
    } else if (opCode === OpCodes.call_indirect || opCode === OpCodes.return_call_indirect) {
      const operands = immediate!.operands;
      if (operands.type !== ImmediateType.IndirectFunction) {
        throw new VerificationError(
          'Error getting funcType for call_indirect, invalid immediate.'
        );
      }
      funcType = operands.funcType;
    }

    return funcType;
  }

  _getStackObjectValueType(
    opCode: OpCodeDef,
    immediate: Immediate | null,
    argCount: number
  ): ValueTypeDescriptor {
    if (
      opCode === OpCodes.get_global ||
      opCode === OpCodes.set_global
    ) {
      const operands = immediate!.operands;
      if (operands.type === ImmediateType.Global && operands.global instanceof GlobalBuilder) {
        return operands.global.valueType;
      } else if (operands.type === ImmediateType.Global && operands.global instanceof ImportBuilder) {
        return (operands.global.data as GlobalType).valueType;
      }
      throw new VerificationError('Invalid operand for global instruction.');
    } else if (
      opCode === OpCodes.get_local ||
      opCode === OpCodes.set_local ||
      opCode === OpCodes.tee_local
    ) {
      const operands = immediate!.operands;
      if (
        operands.type !== ImmediateType.Local ||
        (!(operands.local instanceof LocalBuilder) && !(operands.local instanceof FunctionParameterBuilder))
      ) {
        throw new VerificationError('Invalid operand for local instruction.');
      }
      return operands.local.valueType;
    }

    // ─── GC opcode push type resolution ───

    // struct.get: push the field's value type
    if (opCode === OpCodes.struct_get && this._typeResolver && immediate) {
      const structOperands = immediate.numericOperands();
      const typeIndex = structOperands[0];
      const fieldIndex = structOperands[1];
      const structType = this._typeResolver.getStructType(typeIndex);
      if (structType && fieldIndex < structType.fields.length) {
        return structType.fields[fieldIndex].type;
      }
    }

    // struct.new_default: push a concrete ref type (no field pops needed)
    if (opCode === OpCodes.struct_new_default && immediate) {
      return refType(immediate.numericOperands()[0]);
    }

    // array.new, array.new_default, array.new_data, array.new_elem: push concrete array ref
    if (
      opCode === OpCodes.array_new ||
      opCode === OpCodes.array_new_default ||
      opCode === OpCodes.array_new_data ||
      opCode === OpCodes.array_new_elem
    ) {
      if (immediate) return refType(immediate.numericOperands()[0]);
      return ValueType.AnyRef;
    }

    // array.get: push the array's element type
    if (opCode === OpCodes.array_get && this._typeResolver && immediate) {
      const typeIndex = immediate.numericOperands()[0];
      const arrayType = this._typeResolver.getArrayType(typeIndex);
      if (arrayType) {
        return arrayType.elementType;
      }
    }

    // ref.cast, ref.cast_null: push narrowed ref type based on heap type immediate
    if ((opCode === OpCodes.ref_cast || opCode === OpCodes.ref_cast_null) && immediate) {
      const heapType = immediate.numericOperands()[0];
      if (typeof heapType === 'number') {
        if (heapTypeToValueType[heapType]) return heapTypeToValueType[heapType];
        // Concrete type index
        return refType(heapType);
      }
      return ValueType.AnyRef;
    }

    // ref.i31: push i31ref
    if (opCode === OpCodes.ref_i31) {
      return ValueType.I31Ref;
    }

    // any.convert_extern: push anyref
    if (opCode === OpCodes.any_convert_extern) {
      return ValueType.AnyRef;
    }

    // extern.convert_any: push externref
    if (opCode === OpCodes.extern_convert_any) {
      return ValueType.ExternRef;
    }

    // br_on_cast, br_on_cast_fail: push a ref type (the narrowed or original type)
    if (opCode === OpCodes.br_on_cast || opCode === OpCodes.br_on_cast_fail) {
      return ValueType.AnyRef;
    }

    // ref.null: push the corresponding ref type based on heap type immediate
    if (opCode === OpCodes.ref_null && immediate) {
      const heapType = immediate.numericOperands()[0];
      if (typeof heapType === 'number' && heapTypeToValueType[heapType]) {
        return heapTypeToValueType[heapType];
      }
      return ValueType.AnyRef;
    }

    const stackArgTypes = this._getStackValueTypes(this._operandStack, argCount);
    return stackArgTypes[0];
  }

  /**
   * Check if `actual` is assignable to `expected` considering GC reference subtyping.
   * Subtype hierarchy: anyref ← eqref ← {i31ref, structref, arrayref}
   * Bottom types: nullref → any, nullfuncref → funcref, nullexternref → externref
   */
  _isAssignableTo(actual: ValueTypeDescriptor, expected: ValueTypeDescriptor): boolean {
    if (actual === expected) return true;

    // Concrete ref type matching: (ref $typeA) assignable to (ref $typeB) if A is subtype of B
    if (isConcreteRefType(actual) && isConcreteRefType(expected)) {
      // Nullable check: non-nullable is assignable to nullable, but not vice-versa
      if (actual.refPrefix === 0x64 && expected.refPrefix === 0x63) {
        // (ref $A) → (ref null $B): allowed if A subtype of B
      } else if (actual.refPrefix === 0x63 && expected.refPrefix === 0x64) {
        // (ref null $A) → (ref $B): NOT allowed (nullable to non-nullable)
        return false;
      }
      // Same type index or subtype chain
      if (actual.typeIndex === expected.typeIndex) return true;
      return this._isSubtype(actual.typeIndex, expected.typeIndex);
    }

    // Concrete ref assignable to abstract: struct → structref/eqref/anyref, array → arrayref/eqref/anyref, func → funcref
    if (isConcreteRefType(actual) && !isConcreteRefType(expected)) {
      if (this._typeResolver) {
        const kind = this._typeResolver.getTypeKind?.(actual.typeIndex);
        if (kind === 'struct') {
          return expected === ValueType.StructRef || expected === ValueType.EqRef ||
            expected === ValueType.AnyRef || expected === ValueType.Int32;
        }
        if (kind === 'array') {
          return expected === ValueType.ArrayRef || expected === ValueType.EqRef ||
            expected === ValueType.AnyRef || expected === ValueType.Int32;
        }
        if (kind === 'func') {
          return expected === ValueType.FuncRef || expected === ValueType.Int32;
        }
      }
      // Without type resolver, allow concrete refs to match common supertypes
      if (expected === ValueType.AnyRef || expected === ValueType.EqRef ||
          expected === ValueType.StructRef || expected === ValueType.ArrayRef ||
          expected === ValueType.FuncRef || expected === ValueType.Int32) {
        return true;
      }
    }

    // GC reference type subtyping: anyref ← eqref ← {i31ref, structref, arrayref}
    if (expected === ValueType.AnyRef) {
      return actual === ValueType.EqRef || actual === ValueType.I31Ref ||
        actual === ValueType.StructRef || actual === ValueType.ArrayRef ||
        actual === ValueType.NullRef || isConcreteRefType(actual);
    }
    if (expected === ValueType.EqRef) {
      return actual === ValueType.I31Ref || actual === ValueType.StructRef ||
        actual === ValueType.ArrayRef || actual === ValueType.NullRef ||
        isConcreteRefType(actual);
    }
    if (expected === ValueType.StructRef) {
      return actual === ValueType.NullRef || (isConcreteRefType(actual) &&
        this._typeResolver?.getTypeKind?.(actual.typeIndex) === 'struct');
    }
    if (expected === ValueType.ArrayRef) {
      return actual === ValueType.NullRef || (isConcreteRefType(actual) &&
        this._typeResolver?.getTypeKind?.(actual.typeIndex) === 'array');
    }
    if (expected === ValueType.FuncRef) {
      return actual === ValueType.NullFuncRef || (isConcreteRefType(actual) &&
        this._typeResolver?.getTypeKind?.(actual.typeIndex) === 'func');
    }
    if (expected === ValueType.ExternRef) {
      return actual === ValueType.NullExternRef;
    }
    // Legacy compatibility: many opcodes (table.set, etc.) use Int32 in metadata
    // as a placeholder for reference types. Allow ref types where Int32 is expected.
    if (expected === ValueType.Int32 && (_isRefType(actual) || isConcreteRefType(actual))) {
      return true;
    }
    // Memory64: load/store address operands are i64 instead of i32
    if (expected === ValueType.Int32 && actual === ValueType.Int64 && this._memory64) {
      return true;
    }
    return false;
  }

  _isSubtype(actualIndex: number, expectedIndex: number): boolean {
    if (!this._typeResolver?.getSuperTypes) return false;
    // Walk the supertype chain with cycle detection
    const visited = new Set<number>();
    let current = actualIndex;
    while (true) {
      if (visited.has(current)) return false;
      visited.add(current);
      const supers = this._typeResolver.getSuperTypes(current);
      if (supers.length === 0) return false;
      for (const s of supers) {
        if (s === expectedIndex) return true;
      }
      // Walk up to the first supertype
      current = supers[0];
    }
  }

  _getStackValueTypes(stack: OperandStack, count: number): ValueTypeDescriptor[] {
    const results: ValueTypeDescriptor[] = [];
    let current = stack;

    for (let index = 0; index < count; index++) {
      results.push(current.valueType);
      current = current.pop();
    }

    return results.reverse();
  }

  _formatAndList<T>(values: T[], getText?: (item: T) => string): string {
    if (values.length === 1) {
      return getText ? getText(values[0]) : String(values[0]);
    }

    let text = '';
    for (let index = 0; index < values.length; index++) {
      text += getText ? getText(values[index]) : String(values[index]);
      if (index === values.length - 2) {
        text += ' and ';
      } else if (index !== values.length - 1) {
        text += ', ';
      }
    }

    return text;
  }
}

// Import at the end to avoid circular dependency issues at type level
import type GlobalType from '../GlobalType';
